import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { Vector2d } from 'konva/lib/types';
import { CanvasDimensions, CanvasState, DrawMode } from '@/entities/canvas';
import { VIDEO_ASPECT_RATIO } from '@/shared/lib';

const initialState = (): CanvasState => ({
  drawMode: null,
  dimensions: {
    canvasWidth: 0,
    canvasHeight: 0,
  },
  bgRectangle: null,
  mousePosition: null,
});

export const canvasSlice = createSlice({
  name: 'canvas',
  initialState,
  reducers: {
    updateDimensions(state, action: PayloadAction<CanvasDimensions>) {
      state.dimensions = action.payload;
    },

    updateMousePosition(state, action: PayloadAction<Vector2d | undefined>) {
      state.mousePosition = action.payload || null;
    },

    toggleDrawMode(state, action: PayloadAction<DrawMode | undefined>) {
      if (!action.payload || state.drawMode === action.payload) {
        state.drawMode = null;
      } else {
        state.drawMode = action.payload;
      }
    },

    setBgRectangle(
      state,
      action: PayloadAction<{
        top_left: [number, number];
        bottom_right: [number, number];
      }>
    ) {
      const { top_left, bottom_right } = action.payload;
      const [topLeftX, topLeftY] = top_left;
      const [bottomRightX, bottomRightY] = bottom_right;

      state.bgRectangle = {
        topLeft: {
          x: topLeftX * state.dimensions.canvasWidth,
          y: topLeftY * state.dimensions.canvasHeight,
          relativeX: topLeftX,
          relativeY: topLeftY,
        },
        bottomRight: {
          x: bottomRightX * state.dimensions.canvasWidth,
          y: bottomRightY * state.dimensions.canvasHeight,
          relativeX: bottomRightX,
          relativeY: bottomRightY,
        },
      };
    },

    /** Draw methods */
    drawBackground(state, action: PayloadAction<{ pointer: Vector2d }>) {
      const x = action.payload.pointer.x;
      const y = action.payload.pointer.y;

      if (state.bgRectangle && !state.bgRectangle.bottomRight) {
        // Second click - calculate actual top-left and bottom-right
        const firstPoint = state.bgRectangle.topLeft;

        const topLeftX = Math.min(firstPoint.x, x);
        const topLeftY = Math.min(firstPoint.y, y);
        const bottomRightX = Math.max(firstPoint.x, x);
        const bottomRightY = Math.max(firstPoint.y, y);

        const topLeft = {
          x: topLeftX,
          y: topLeftY,
          relativeX: topLeftX / state.dimensions.canvasWidth,
          relativeY:
            (topLeftY * VIDEO_ASPECT_RATIO) / state.dimensions.canvasWidth,
        };

        const bottomRight = {
          x: bottomRightX,
          y: bottomRightY,
          relativeX: bottomRightX / state.dimensions.canvasWidth,
          relativeY:
            (bottomRightY * VIDEO_ASPECT_RATIO) / state.dimensions.canvasWidth,
        };

        state.bgRectangle = {
          topLeft,
          bottomRight,
        };
      } else {
        // First click - just store the point
        state.bgRectangle = {
          topLeft: {
            x: x,
            y: y,
            relativeX: x / state.dimensions.canvasWidth,
            relativeY: (y * VIDEO_ASPECT_RATIO) / state.dimensions.canvasWidth,
          },
          bottomRight: null,
        };
      }
    },
  },
});

export const {
  updateDimensions,
  drawBackground,
  toggleDrawMode,
  updateMousePosition,
  setBgRectangle,
} = canvasSlice.actions;
